import { useSyncExternalStore } from "react";
const METRICS_PROTOCOL = "lens-engine-metrics/v1";
const MEASUREMENT_PROTOCOL = "lens-engine-measurement/v1";
const DEFAULT_PUBLISH_INTERVAL_MS = 1_000;
const DEFAULT_HISTOGRAM_MAX_MS = 2_000;
const DEFAULT_SLOW_FRAME_THRESHOLD_MS = 200;
const LONG_FRAME_GAP_MS = 1_000;
function finiteNonNegative(value) {
    const number = Number(value);
    return Number.isFinite(number) && number >= 0 ? number : null;
}
function createAccumulator(histogramMaxMs, slowFrameThresholdMs) {
    return {
        count: 0,
        missingCount: 0,
        mean: 0,
        d2: 0,
        latest: null,
        max: 0,
        slowFrameCount: 0,
        overflowCount: 0,
        histogram: new Uint32Array(histogramMaxMs + 2),
        histogramMaxMs,
        slowFrameThresholdMs,
    };
}
function recordProcessingTime(accumulator, processingTimeMs) {
    const value = finiteNonNegative(processingTimeMs);
    if (value === null) {
        accumulator.missingCount += 1;
        return false;
    }
    const difference = value - accumulator.mean;
    accumulator.count += 1;
    accumulator.mean += difference / accumulator.count;
    accumulator.d2 += difference * (value - accumulator.mean);
    accumulator.latest = value;
    accumulator.max = Math.max(accumulator.max, value);
    if (value >= accumulator.slowFrameThresholdMs)
        accumulator.slowFrameCount += 1;
    const rounded = Math.round(value);
    const bucket = Math.min(rounded, accumulator.histogramMaxMs + 1);
    if (bucket > accumulator.histogramMaxMs)
        accumulator.overflowCount += 1;
    accumulator.histogram[bucket] += 1;
    return true;
}
function histogramQuantile(accumulator, quantile) {
    if (!accumulator.count)
        return 0;
    const target = Math.max(1, Math.ceil(accumulator.count * quantile));
    let seen = 0;
    for (let bucket = 0; bucket < accumulator.histogram.length; bucket += 1) {
        seen += accumulator.histogram[bucket];
        if (seen >= target)
            return bucket;
    }
    return accumulator.histogramMaxMs + 1;
}
function processingSnapshot(accumulator) {
    return Object.freeze({
        latestMs: accumulator.latest,
        meanMs: accumulator.count ? accumulator.mean : 0,
        stdDevMs: accumulator.count ? Math.sqrt(accumulator.d2 / accumulator.count) : 0,
        medianMs: histogramQuantile(accumulator, 0.5),
        p95Ms: histogramQuantile(accumulator, 0.95),
        p99Ms: histogramQuantile(accumulator, 0.99),
        maxMs: accumulator.count ? accumulator.max : 0,
        sampleCount: accumulator.count,
        missingSampleCount: accumulator.missingCount,
        slowFrameThresholdMs: accumulator.slowFrameThresholdMs,
        slowFrameCount: accumulator.slowFrameCount,
        overflowThresholdMs: accumulator.histogramMaxMs,
        overflowCount: accumulator.overflowCount,
    });
}
function createMeasurement(activeMeasurements, now, options, defaults) {
    const label = options.label == null ? null : String(options.label);
    const warmupFrames = Math.max(0, Math.trunc(Number(options.warmupFrames) || 0));
    const histogramMaxMs = Math.max(1, Math.trunc(Number(options.histogramMaxMs) || defaults.histogramMaxMs));
    const slowFrameThresholdMs = finiteNonNegative(options.slowFrameThresholdMs) ?? defaults.slowFrameThresholdMs;
    let accumulator = createAccumulator(histogramMaxMs, slowFrameThresholdMs);
    let active = true;
    let startedAtMs = now();
    let framesSeen = 0;
    let includedFrameCount = 0;
    let previousFrameAtMs = null;
    let validFrameIntervalCount = 0;
    let validFrameIntervalTotalMs = 0;
    let excludedLongGapCount = 0;
    let smoothedFps = 0;
    const measurement = {
        get active() { return active; },
        get label() { return label; },
        record(event, completedAtMs) {
            if (!active)
                return;
            framesSeen += 1;
            if (framesSeen <= warmupFrames)
                return;
            includedFrameCount += 1;
            recordProcessingTime(accumulator, event?.processingTimeMs);
            if (previousFrameAtMs !== null) {
                const elapsed = completedAtMs - previousFrameAtMs;
                if (elapsed > 0 && elapsed < LONG_FRAME_GAP_MS) {
                    const instantaneousFps = 1_000 / elapsed;
                    smoothedFps = (smoothedFps + instantaneousFps) / 2;
                    validFrameIntervalCount += 1;
                    validFrameIntervalTotalMs += elapsed;
                }
                else if (elapsed >= LONG_FRAME_GAP_MS) {
                    excludedLongGapCount += 1;
                }
            }
            previousFrameAtMs = completedAtMs;
        },
        measure() {
            const measuredAtMs = now();
            const frameCallbackFps = validFrameIntervalTotalMs > 0
                ? (validFrameIntervalCount * 1_000) / validFrameIntervalTotalMs
                : 0;
            return Object.freeze({
                protocol: MEASUREMENT_PROTOCOL,
                label,
                active,
                startedAtMs,
                measuredAtMs,
                elapsedMs: Math.max(0, measuredAtMs - startedAtMs),
                framesSeen,
                warmupFrames,
                frameCount: includedFrameCount,
                frameCallbackFps,
                smoothedFrameCallbackFps: smoothedFps,
                outputFps: frameCallbackFps,
                smoothedFps,
                validFrameIntervalCount,
                excludedLongGapCount,
                processingTimeMs: processingSnapshot(accumulator),
            });
        },
        reset() {
            accumulator = createAccumulator(histogramMaxMs, slowFrameThresholdMs);
            startedAtMs = now();
            framesSeen = 0;
            includedFrameCount = 0;
            previousFrameAtMs = null;
            validFrameIntervalCount = 0;
            validFrameIntervalTotalMs = 0;
            excludedLongGapCount = 0;
            smoothedFps = 0;
        },
        end() {
            if (!active)
                return;
            active = false;
            activeMeasurements.delete(measurement);
        },
    };
    activeMeasurements.add(measurement);
    return measurement;
}
/**
 * Shared, low-overhead LensCore performance store.
 *
 * Every frame updates O(1) aggregates and active measurement handles. React
 * listeners are notified only at the configured publication interval or by an
 * explicit flush, so metrics collection never forces per-frame rerenders.
 */
export function createEngineMetricsStore(options = {}) {
    const now = options.now ?? (() => performance.now());
    const requestedPublishIntervalMs = Number(options.publishIntervalMs ?? DEFAULT_PUBLISH_INTERVAL_MS);
    const publishIntervalMs = Number.isFinite(requestedPublishIntervalMs) && requestedPublishIntervalMs >= 0
        ? requestedPublishIntervalMs
        : DEFAULT_PUBLISH_INTERVAL_MS;
    const requestedHistogramMaxMs = Number(options.histogramMaxMs ?? DEFAULT_HISTOGRAM_MAX_MS);
    const histogramMaxMs = Number.isFinite(requestedHistogramMaxMs) && requestedHistogramMaxMs >= 1
        ? Math.trunc(requestedHistogramMaxMs)
        : DEFAULT_HISTOGRAM_MAX_MS;
    const slowFrameThresholdMs = finiteNonNegative(options.slowFrameThresholdMs) ?? DEFAULT_SLOW_FRAME_THRESHOLD_MS;
    const listeners = new Set();
    const activeMeasurements = new Set();
    let accumulator = createAccumulator(histogramMaxMs, slowFrameThresholdMs);
    let frameCount = 0;
    let previousFrameAtMs = null;
    let fpsWindowStartedAtMs = null;
    let fpsWindowIntervals = 0;
    let outputFps = 0;
    let smoothedFps = 0;
    let snapshotCache = null;
    function snapshot() {
        if (snapshotCache)
            return snapshotCache;
        snapshotCache = Object.freeze({
            protocol: METRICS_PROTOCOL,
            frameCount,
            frameCallbackFps: outputFps,
            smoothedFrameCallbackFps: smoothedFps,
            outputFps,
            smoothedFps,
            lastFrameCompletedAtMs: previousFrameAtMs,
            processingTimeMs: processingSnapshot(accumulator),
        });
        return snapshotCache;
    }
    function publish() {
        snapshotCache = null;
        const next = snapshot();
        for (const listener of listeners) {
            try {
                listener();
            }
            catch { /* metrics observers must never break LensCore's frame callback */ }
        }
        return next;
    }
    const store = {
        get: snapshot,
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        recordFrame(event, count) {
            const completedAtMs = now();
            frameCount = Number.isFinite(Number(count)) ? Number(count) : frameCount + 1;
            recordProcessingTime(accumulator, event?.processingTimeMs);
            for (const measurement of activeMeasurements)
                measurement.record(event, completedAtMs);
            if (previousFrameAtMs !== null) {
                const elapsed = completedAtMs - previousFrameAtMs;
                if (elapsed > 0 && elapsed < LONG_FRAME_GAP_MS) {
                    const instantaneousFps = 1_000 / elapsed;
                    smoothedFps = (smoothedFps + instantaneousFps) / 2;
                    fpsWindowIntervals += 1;
                }
                else if (elapsed >= LONG_FRAME_GAP_MS) {
                    // Ignore background-tab/camera gaps instead of reporting a false
                    // near-zero frame rate for the next publication window.
                    fpsWindowStartedAtMs = completedAtMs;
                    fpsWindowIntervals = 0;
                }
            }
            previousFrameAtMs = completedAtMs;
            if (fpsWindowStartedAtMs === null)
                fpsWindowStartedAtMs = completedAtMs;
            const fpsElapsedMs = completedAtMs - fpsWindowStartedAtMs;
            if (fpsElapsedMs >= publishIntervalMs) {
                outputFps = fpsElapsedMs > 0 ? (fpsWindowIntervals * 1_000) / fpsElapsedMs : 0;
                fpsWindowStartedAtMs = completedAtMs;
                fpsWindowIntervals = 0;
                publish();
            }
        },
        beginMeasurement(measurementOptions = {}) {
            return createMeasurement(activeMeasurements, now, measurementOptions, {
                histogramMaxMs,
                slowFrameThresholdMs,
            });
        },
        flush() {
            return publish();
        },
        reset() {
            accumulator = createAccumulator(histogramMaxMs, slowFrameThresholdMs);
            frameCount = 0;
            previousFrameAtMs = null;
            fpsWindowStartedAtMs = null;
            fpsWindowIntervals = 0;
            outputFps = 0;
            smoothedFps = 0;
            return publish();
        },
        destroy() {
            for (const measurement of [...activeMeasurements])
                measurement.end();
            listeners.clear();
        },
    };
    return store;
}
const identitySelector = (state) => state;
export function useEngineMetricsFromStore(store, selector = identitySelector) {
    return useSyncExternalStore(store.subscribe, () => selector(store.get()), () => selector(store.get()));
}
export { DEFAULT_HISTOGRAM_MAX_MS, DEFAULT_PUBLISH_INTERVAL_MS, DEFAULT_SLOW_FRAME_THRESHOLD_MS, MEASUREMENT_PROTOCOL, METRICS_PROTOCOL, };
//# sourceMappingURL=engineMetrics.js.map